<template>
  <v-row v-if="loading" justify="center">
    <v-col cols="auto" class="mt-8">
      <v-progress-circular indeterminate :size="50" color="primary" />
    </v-col>
  </v-row>
  <list-content v-else>
    <v-col cols="12">
      <v-row v-if="startPage.htmlText" density="comfortable">
        <v-col ref="html" class="pa-2 pb-5" v-html="startPage.htmlText" />
      </v-row>

      <v-row density="comfortable">
        <v-col cols="12">
          <template v-if="startPage.featuredProducts.length > 0">
            <v-row justify="center" density="comfortable">
              <v-col
                v-for="(product, index) in startPage.featuredProducts"
                :key="product.productId"
                :cols="cardsCols"
                :sm="cardsSm"
                :md="cardsMd"
                :xl="cardsXl"
              >
                <product-card
                  :product="product"
                  :show-product-identifiers="false"
                  small
                  :add-to-cart-event="StartPageEvent.AddToCart"
                  :loading-realtime-price="loadingRealtimePrices.has(product.productId)"
                  :tabindex="index === 0 ? 0 : -1"
                  show-realtime-loading-indicator
                  :is-focused="currentFocusIndex === index"
                  @keydown="onKeyDown"
                  @focus="onItemFocus(index)"
                  @blur="onItemBlur(index)"
                />
              </v-col>
            </v-row>

            <v-row
              v-if="loadingProducts || startPage.featuredProducts.length === page * PAGE_SIZE"
              density="comfortable"
              justify="center"
            >
              <v-col>
                <v-btn
                  :loading="loadingProducts ? 'primary' : false"
                  size="large"
                  block
                  @click="getMoreProducts"
                >
                  {{ $t('moreUppercase') + $t('ellipsis') }}
                </v-btn>
              </v-col>
            </v-row>
          </template>

          <v-row
            v-if="startPage.startPageType === StartPageType.CATEGORIES && categories.length > 0"
            density="comfortable"
          >
            <v-col
              v-for="category in categories"
              :key="category.id"
              cols="12"
              md="6"
              xl="4"
              class="pa-1"
            >
              <start-page-category :category="category" />
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-col>
  </list-content>
</template>

<script setup lang="ts">
import ListContent from '@/components/list/ListContent.vue'
import { useTitle } from '@/helpers'
import { categoriesQueryOptions } from '@/modules/categories/useCategoriesColada'
import ProductCard from '@/modules/product/components/ProductCard.vue'
import useRealtimePrices from '@/modules/product/useRealtimePrices'
import { realtimePricesBatchedQuery } from '@/modules/product/useRealtimePricesColada'
import StartPageCategory from '@/modules/startPage/components/StartPageCategory.vue'
import useStartPage from '@/modules/startPage/useStartPage'
import StartPageEvent from '@/modules/tracking/events/startPage'
import { PAGE_SIZE } from '@/types'
import { StartPageType } from '@/types/index'
import { useArrowKeyGridNavigation } from '@/utils/useArrowKeyGridNavigation'
import { useQuery } from '@pinia/colada'
import { storeToRefs } from 'pinia'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { useDisplay } from 'vuetify'

const currentRoute = useRoute()
const startPageSerial = currentRoute.query.startPageSerial as string

const { t } = useI18n()
useTitle(ref(t('windowTitle.StartPage')))

const startPageStore = useStartPage()
startPageStore.loadStartPage(1, startPageSerial)

const page = ref(1)
const { startPage, loading, loadingProducts } = storeToRefs(startPageStore)

// für diese Ids werden Preise angefragt
const requestedRealtimePriceIds = ref(new Set<number>())

const { data: realtimePricesData, params: realtimePricesParams } = realtimePricesBatchedQuery()

const { data: categoriesData } = useQuery(categoriesQueryOptions())
const categories = computed(() => categoriesData.value ?? [])

const { coladaLoading: loadingRealtimePrices } = storeToRefs(useRealtimePrices())

// Produkt-IDs ändern sich → Realtime-Prices laden
watch(
  () => startPage.value.featuredProducts?.map((product) => product.productId) ?? [],
  (productIds) => {
    const newProductIds = productIds.filter((id) => !requestedRealtimePriceIds.value.has(id))

    if (newProductIds.length === 0) {
      return
    }

    newProductIds.forEach((id) => requestedRealtimePriceIds.value.add(id))

    // dadurch ändert sich der Query-Key: REALTIME_PRICES_QUERY_KEYS.batchFiltered(params.value)
    realtimePricesParams.value = {
      ids: new Set(newProductIds),
      filters: undefined
    }
  }
)

// realtime-price Antwort kommt zurück → jedes Preis-Update in startPage.featuredProducts übernehmen
watch(
  () => realtimePricesData.value,
  (newRealtimePricesData) => {
    if (!newRealtimePricesData) {
      return
    }

    newRealtimePricesData.data.forEach((update) => {
      startPageStore.updateProduct(update)
    })
  }
)

const getMoreProducts = () => {
  page.value++
  startPageStore.loadStartPageNextPage(page.value, startPageSerial)
}

const { smAndDown, xlAndUp } = useDisplay()

const cardsCols = 12
const cardsSm = 6
const cardsMd = 3
const cardsXl = 2

const cardsPerRow = computed(() => {
  if (smAndDown.value) return cardsCols / cardsSm
  if (xlAndUp.value) return cardsCols / cardsXl
  return cardsCols / cardsMd
})

const numberOfProducts = computed(() => startPage.value.featuredProducts.length)

const { currentFocusIndex, onItemFocus, onItemBlur, onKeyDown } = useArrowKeyGridNavigation(
  cardsPerRow,
  numberOfProducts
)
</script>
